/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import type { Theme } from '@coursera/cds-core';
import { Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import { buildCanonicalUrl } from 'bundles/common/utils/urlUtils';
import type { Asset, Maybe } from 'bundles/xddp/__generated__/baseContentfulTypes';
import { TestimonialCardsVariant } from 'bundles/xddp/constants';

const styles = {
  testimonialCardContainer: (_: Theme, variant: TestimonialCardsVariant) => css`
    display: flex;
    padding: var(--cds-spacing-300);
    background-color: #f5f7f8;
    border-radius: 10px;
    min-height: ${variant === TestimonialCardsVariant.DOUBLE ? '200px' : '250px'};
    width: ${variant === TestimonialCardsVariant.DOUBLE ? '800px' : '700px'};

    &:not(:last-child) {
      margin-right: var(--cds-spacing-200);
      ${breakpoints.down(breakpoints.values.md)} {
        margin-right: 0;
      }
    }
    ${breakpoints.down(breakpoints.values.lg)} {
      margin: var(--cds-spacing-150) var(--cds-spacing-150);
      width: ${variant === TestimonialCardsVariant.DOUBLE ? '650px' : '600px'};
      align-items: flex-start;
      height: auto;
    }
    ${breakpoints.down(breakpoints.values.md)} {
      margin: var(--cds-spacing-150) 0;
      width: 100%;
      height: auto;
      min-height: auto;
    }
  `,
  image: (_: Theme, variant: TestimonialCardsVariant) => css`
    padding-top: var(--cds-spacing-200);

    > img {
      object-fit: cover;
      border-radius: 50%;
      border: 3px solid #fff;
      width: ${variant === TestimonialCardsVariant.DOUBLE ? '133px' : '80px'};
      height: ${variant === TestimonialCardsVariant.DOUBLE ? '133px' : '80px'};
    }
    ${breakpoints.down(breakpoints.values.sm)} {
      > img {
        width: 50px;
        height: 50px;
      }
    }
    ${breakpoints.down(480)} {
      > img {
        display: none;
      }
    }
  `,
  testimonialTextSection: () => css`
    display: flex;
    flex-direction: column;
    padding: 0 var(--cds-spacing-300);
    ${breakpoints.down(breakpoints.values.sm)} {
      padding: 0 var(--cds-spacing-100);
    }
  `,
  baseSection: (_: Theme, variant: TestimonialCardsVariant) => css`
    margin-top: auto;
    display: flex;
    justify-content: ${variant === TestimonialCardsVariant.DOUBLE ? 'space-between' : 'unset'};
    flex-direction: ${variant === TestimonialCardsVariant.DOUBLE ? 'row' : 'column'};
    align-items: end;
    ${breakpoints.down(breakpoints.values.lg)} {
      flex-direction: column;
    }
    ${breakpoints.down(breakpoints.values.sm)} {
      align-items: end;
    }
  `,
  testimonialText: () => css`
    margin-top: var(--cds-spacing-200);
  `,
  linkText: css`
    font-size: 14px;
    max-width: 300px;
    text-align: right;
  `,
};

const generateProductLink = (slug?: Maybe<string>, type?: Maybe<string>) => {
  return buildCanonicalUrl(`${type}/${slug}`);
};

type Props = {
  variant?: TestimonialCardsVariant;
  authorName?: Maybe<string>;
  productName?: Maybe<string>;
  text?: Maybe<string>;
  productTypes?: Maybe<Array<Maybe<string>>>;
  productSlug?: Maybe<string>;
  image?: Maybe<Asset>;
};

const TestimonialCard: React.FC<Props> = ({
  image,
  authorName,
  productName,
  text,
  productTypes,
  productSlug,
  variant = TestimonialCardsVariant.DOUBLE,
}) => {
  const theme = useTheme();
  const productLink = generateProductLink(productSlug, productTypes?.[0]);

  return (
    <div css={styles.testimonialCardContainer(theme, variant)} data-testid="testimonial-card-container">
      {image?.url && (
        <div css={styles.image(theme, variant)} data-testid="testimonial-card-img-container">
          <Imgix src={image?.url} alt={image?.title || ''} loading="lazy" />
        </div>
      )}
      <div css={styles.testimonialTextSection} data-testid="testimonial-card-text-section">
        <Typography2 component="p" variant="bodySecondary" color="body" css={styles.testimonialText}>
          {text}
        </Typography2>
        <div css={styles.baseSection(theme, variant)}>
          <Typography2 component="h3" variant="subtitleMedium" color="body">{`— ${authorName}`}</Typography2>
          <Typography2 color="supportText" component="a" href={productLink} css={styles.linkText}>
            {productName}
          </Typography2>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;
